import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { InlineLoadingComponent } from '../../../shared/ui/inline-loading.component';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, InlineLoadingComponent],
  template: `
    <section class="forgot-shell">
      <div class="card">
        <h1>Forgot password</h1>
        <p class="subtitle">Enter your email and we’ll send a reset link.</p>

        <label for="email" class="label">Email</label>
        <input
          id="email"
          type="email"
          placeholder="you@company.com"
          [disabled]="loading()"
          [(ngModel)]="email"
        />

        <button type="button" (click)="submit()" [disabled]="loading()">
          {{ loading() ? 'Sending…' : 'Send reset link' }}
        </button>

        @if (message()) {
          <p class="info">{{ message() }}</p>
        }
        @if (error()) {
          <p class="error">{{ error() }}</p>
        }

        <p class="footer">
          <a routerLink="/login">Back to sign in</a>
        </p>
      </div>

      @if (loading()) {
        <app-inline-loading label="Requesting reset link..." />
      }
    </section>
  `,
  styles: [
    `
      .forgot-shell {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 24px;
        background: #f5f5f5;
      }
      .card {
        width: min(480px, 100%);
        background: #fff;
        border-radius: 12px;
        padding: 28px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
      }
      h1 {
        margin: 0 0 6px;
        font-size: 1.4rem;
        font-weight: 900;
      }
      .subtitle {
        margin: 0 0 18px;
        color: rgba(0, 0, 0, 0.6);
      }
      .label {
        display: block;
        margin: 10px 0 6px;
        font-weight: 700;
      }
      input {
        width: 100%;
        padding: 12px 14px;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        outline: none;
      }
      button {
        width: 100%;
        margin-top: 14px;
        padding: 12px 14px;
        border: none;
        border-radius: 10px;
        background: #1e523f;
        color: #fff;
        font-weight: 800;
        cursor: pointer;
      }
      .info {
        margin-top: 14px;
        color: #1e523f;
      }
      .error {
        margin-top: 14px;
        color: #b42318;
      }
      .footer {
        margin-top: 16px;
        text-align: center;
      }
      a {
        color: #1e523f;
        text-decoration: none;
        font-weight: 700;
      }
    `,
  ],
})
export class ForgotPasswordComponent {
  email = '';
  readonly loading = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  constructor(private readonly authService: AuthService) {}

  async submit(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);

    try {
      const email = (this.email ?? '').trim();
      if (!email) {
        this.error.set('Email is required.');
        return;
      }
      await this.authService.requestPasswordReset(email);
      this.message.set(
        'If an account exists, we’ve emailed a reset link. Check Spam/Promotions if you don’t see it in a minute.',
      );
    } catch (err) {
      this.error.set(this.authService.resolveAuthError(err));
    } finally {
      this.loading.set(false);
    }
  }
}

